/**
 * Vanity-username helpers.
 *
 * The seller's display name doubles as their public handle: we derive a
 * normalized `username` (lowercase, ASCII, hyphen-separated) from it and
 * store it in `publicData.username`. That normalized form is the
 * canonical key — it's what we check for uniqueness and what the vanity
 * profile URL (`/u/:username`) resolves against. Normalizing (rather
 * than storing the raw display name) means "Åse" and "ase", or "Jo Doe"
 * and "jo-doe", can't split into two profiles or collide silently.
 */

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 40;
export const USERNAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Letters that don't decompose under NFKD (so we map them explicitly).
// Nordic + a few common Slavic/German ones seen in Norwegian names.
const CHAR_MAP = {
  æ: 'ae',
  ø: 'o',
  å: 'a',
  ä: 'a',
  ö: 'o',
  ü: 'u',
  ß: 'ss',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ł: 'l',
};

// Handles a username can't take — route segments + impersonation-prone
// words. The vanity URL lives under /u/, so these mostly guard against
// impersonation and confusing handles rather than route clashes.
export const RESERVED_USERNAMES = new Set([
  'admin',
  'administrator',
  'support',
  'help',
  'moderator',
  'mod',
  'official',
  'zuprize',
  'team',
  'staff',
  'api',
  'www',
  'root',
  'system',
  'null',
  'undefined',
  'user',
  'users',
  'profile',
  'account',
  'settings',
  'login',
  'logout',
  'signup',
  'search',
  'checkout',
  'order',
  'sale',
  'inbox',
  'listing',
  'listings',
  'about',
  'terms',
  'privacy',
]);

/**
 * Turn a free-text display name into a normalized username slug.
 * Lowercases, transliterates accented/Nordic letters to ASCII, replaces
 * any run of non-alphanumerics with a single hyphen, and trims hyphens.
 * Returns '' when nothing usable remains.
 *
 * @param {string} displayName
 * @returns {string}
 */
export const displayNameToUsername = displayName => {
  if (!displayName || typeof displayName !== 'string') {
    return '';
  }
  const lower = displayName.toLowerCase().trim();
  const mapped = Array.from(lower)
    .map(ch => (Object.prototype.hasOwnProperty.call(CHAR_MAP, ch) ? CHAR_MAP[ch] : ch))
    .join('');
  return mapped
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip remaining combining diacritics
    .replace(/[^a-z0-9]+/g, '-') // any non-alphanumeric run -> single hyphen
    .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
};

/** True when the slug is the right length + charset (not uniqueness/reserved). */
export const isValidUsername = username =>
  typeof username === 'string' &&
  username.length >= USERNAME_MIN_LENGTH &&
  username.length <= USERNAME_MAX_LENGTH &&
  USERNAME_PATTERN.test(username);

/** True when the slug is on the reserved list. */
export const isReservedUsername = username =>
  typeof username === 'string' && RESERVED_USERNAMES.has(username.toLowerCase());
